"use client";

import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  CircularProgress,
  Grid,
  Menu,
  MenuItem,
  IconButton,
  Chip,
} from "@mui/material";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTranslation } from "react-i18next";

// Dairesel ilerleme grafiği bileşeni
const CircularProgressChart = ({ value, color, size = 80 }) => {
  // SVG için hesaplamalar
  const radius = size / 2 - 8;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - value / 100);

  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Arka plan dairesi */}
      <svg width={size} height={size} style={{ position: "absolute" }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255, 255, 255, 0.2)"
          strokeWidth={6}
        />
      </svg>

      {/* İlerleme dairesi */}
      <svg
        width={size}
        height={size}
        style={{ position: "absolute", transform: "rotate(-90deg)" }}
      >
        <defs>
          <linearGradient
            id={`gradient-${color}`}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="rgba(255, 255, 255, 0.6)" />
            <stop offset="100%" stopColor={color} />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#gradient-${color})`}
          strokeWidth={6}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </svg>

      {/* Yüzde değeri */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
          color: "white",
          fontSize: "1.2rem",
        }}
      >
        {`${value}%`}
      </Typography>
    </Box>
  );
};

const FinancialSummaryCharts = ({
  recentActivities,
  loading,
  lightMode,
  timeFilter,
  userFilter,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  const [financialData, setFinancialData] = useState([]);
  const [totalAmounts, setTotalAmounts] = useState({
    totalInvoiceAmount: 0,
    totalOutstandingAmount: 0,
    totalOverdueInvoiceAmount: 0,
    totalOverduePaymentAmount: 0,
    fullyPaidAmount: 0,
    depositPaidAmount: 0,
  });
  const [chartType, setChartType] = useState("bar");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Para formatı
  const paraFormat = (number) => {
    const formatter = new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    });
    return formatter.format(number);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChartTypeChange = (type) => {
    setChartType(type);
    handleClose();
  };

  // Zaman ve kullanıcı filtresine göre verileri filtreleme
  const filterData = (data, dateField) => {
    if (!data || !data.length) return [];

    let filteredData = [...data];

    // Zaman filtresini uygula
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);

    if (timeFilter === "today") {
      filteredData = filteredData.filter((item) => {
        const itemDate = new Date(item[dateField]);
        itemDate.setHours(0, 0, 0, 0);
        return itemDate.getTime() === today.getTime();
      });
    } else if (timeFilter === "yesterday") {
      filteredData = filteredData.filter((item) => {
        const itemDate = new Date(item[dateField]);
        itemDate.setHours(0, 0, 0, 0);
        return itemDate.getTime() === yesterday.getTime();
      });
    } else if (timeFilter === "week") {
      filteredData = filteredData.filter((item) => {
        const itemDate = new Date(item[dateField]);
        return itemDate >= lastWeek;
      });
    }

    // Kullanıcı filtresini uygula
    if (userFilter !== "all") {
      filteredData = filteredData.filter(
        (item) => item.student_name === userFilter
      );
    }

    return filteredData;
  };

  useEffect(() => {
    if (recentActivities) {
      // Zaman ve kullanıcı filtresine göre verileri filtrele
      const filteredInvoices = filterData(
        recentActivities.recent_invoices,
        "created_at"
      );
      const filteredInvoicesOverdue = filterData(
        recentActivities.recent_invoices_overdue,
        "due_date"
      );
      const filteredPaymentsOverdue = filterData(
        recentActivities.recent_payments_overdue,
        "due_date"
      );
      const filteredQuotes = filterData(
        recentActivities.recent_quotes,
        "created_at"
      );

      // Calculate total invoice amount
      let totalInvoiceAmount = 0;
      let totalOutstandingAmount = 0;
      let totalOverdueInvoiceAmount = 0;
      let totalOverduePaymentAmount = 0;
      let fullyPaidAmount = 0;
      let depositPaidAmount = 0;

      // Calculate from recent invoices
      if (filteredInvoices) {
        filteredInvoices.forEach((invoice) => {
          totalInvoiceAmount += invoice.price || 0;

          if (invoice.status === "fullypaid") {
            fullyPaidAmount += invoice.price || 0;
          } else if (invoice.status === "depositpaid") {
            depositPaidAmount += invoice.price || 0;
          }

          totalOutstandingAmount += invoice.outstanding || 0;
        });
      }

      // Calculate from overdue invoices
      if (filteredInvoicesOverdue) {
        filteredInvoicesOverdue.forEach((invoice) => {
          totalOverdueInvoiceAmount += invoice.outstanding || 0;
        });
      }

      // Calculate from overdue payments
      if (filteredPaymentsOverdue) {
        filteredPaymentsOverdue.forEach((payment) => {
          totalOverduePaymentAmount += payment.outstanding_amount || 0;
        });
      }

      setTotalAmounts({
        totalInvoiceAmount,
        totalOutstandingAmount,
        totalOverdueInvoiceAmount,
        totalOverduePaymentAmount,
        fullyPaidAmount,
        depositPaidAmount,
      });

      // Prepare data for charts - Kısaltılmış isimler kullan
      const chartData = [
        {
          name: t("totalInvoices"),
          shortName: t("invoices"),
          value: totalInvoiceAmount,
          color: "#2196f3",
          gradientStart: "#64B5F6",
          gradientEnd: "#1976D2",
        },
        {
          name: t("outstanding"),
          shortName: t("outstanding"),
          value: totalOutstandingAmount,
          color: "#ff9800",
          gradientStart: "#FFB74D",
          gradientEnd: "#F57C00",
        },
        {
          name: t("overdueInvoices"),
          shortName: t("overdueInvoice"),
          value: totalOverdueInvoiceAmount,
          color: "#f44336",
          gradientStart: "#E57373",
          gradientEnd: "#D32F2F",
        },
        {
          name: t("overduePayment"),
          shortName: t("overduePayment"),
          value: totalOverduePaymentAmount,
          color: "#e91e63",
          gradientStart: "#F06292",
          gradientEnd: "#C2185B",
        },
        {
          name: t("fullPaid"),
          shortName: t("fullPaid"),
          value: fullyPaidAmount,
          color: "#4caf50",
          gradientStart: "#81C784",
          gradientEnd: "#388E3C",
        },
        {
          name: t("deposit"),
          shortName: t("deposit"),
          value: depositPaidAmount,
          color: "#9c27b0",
          gradientStart: "#BA68C8",
          gradientEnd: "#7B1FA2",
        },
      ];


      setFinancialData(chartData);
    }
  }, [recentActivities, timeFilter, userFilter, i18n.language]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: lightMode ? "#fff" : "#333",
            border: "1px solid #ccc",
            p: 1,
            borderRadius: 1,
          }}
        >
          <Typography
            variant="caption"
            color="textPrimary"
            sx={{ fontWeight: "bold" }}
          >
            {payload[0].payload.name} 
          </Typography>
          <Typography variant="caption" display="block" color="textSecondary">
            {paraFormat(payload[0].value)}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
    name,
    value,
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null; // Don't show labels for small slices

    return (
      <text
        x={x}
        y={y}
        fill="#fff"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        style={{ fontSize: "10px" }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const renderChart = () => {
    switch (chartType) {
      case "pie":
        return (
          <Box sx={{ width: "100%", height: 300 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={500}>
              <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                <defs>
                  {financialData.map((entry, index) => (
                    <linearGradient
                      key={`gradient-${index}`}
                      id={`pieGradient-${index}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor={entry.gradientStart} />
                      <stop offset="100%" stopColor={entry.gradientEnd} />
                    </linearGradient>
                  ))}
                </defs>
                <Pie
                  data={financialData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {financialData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={`url(#pieGradient-${index})`}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  formatter={(value, entry, index) => (
                    <span
                      style={{
                        fontSize: "10px",
                        color: lightMode ? "#333" : "#fff",
                        textTransform: 'capitalize'
                      }}
                    >
                      {financialData[index]?.shortName || value}
                    </span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        );
      case "bar":
      default:
        return (
          <Box sx={{ width: "100%", height: 300 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={600}>
              <BarChart
                data={financialData}
                margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
              >
                <defs>
                  {financialData.map((entry, index) => (
                    <linearGradient
                      key={`gradient-${index}`}
                      id={`barGradient-${index}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor={entry.gradientStart} />
                      <stop offset="100%" stopColor={entry.gradientEnd} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="shortName"
                  tick={{ fontSize: 10 }}
                  height={40}
                  tickMargin={5}
                />
                <YAxis
                  tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 10 }}
                  width={40}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="top"
                  height={36}
                  formatter={(value) => (
                    <span
                      style={{
                        fontSize: "10px",
                        color: lightMode ? "#333" : "#fff",
                      }}
                    >
                      {value}
                    </span>
                  )}
                />
                <Bar
                  dataKey="value"
                  name={t("amount")}
                  barSize={20}
                  radius={[10, 10, 0, 0]}
                >
                  {financialData.map((entry, index) => (
                    <Cell

                      key={`cell-${index}`}
                      fill={`url(#barGradient-${index})`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        );
    }
  };

  const getChartTypeLabel = () => {
    switch (chartType) {
      case "pie":
        return t("pieChart");
      case "bar":
      default:
        return t("barChart");
    }
  };

  const getChartTypeIcon = () => {
    switch (chartType) {
      case "pie":
        return (
          <Box
            component="span"
            sx={{ mr: 1, display: "inline-flex", alignItems: "center" }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4V12H20C20 16.41 16.41 20 12 20Z"
                fill="currentColor"
              />
            </svg>
          </Box>
        );
      case "bar":
      default:
        return (
          <Box
            component="span"
            sx={{ mr: 1, display: "inline-flex", alignItems: "center" }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 20V10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 20V4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6 20V14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>
        );
    }
  };

  // Zaman filtresi etiketini göster
  const getTimeFilterLabel = () => {
    switch (timeFilter) {
      case "today":
        return t("today");
      case "yesterday":
        return t("yesterday");
      case "week":
        return t("thisWeek");
      case "all":
      default:
        return t("allTime");
    }
  };

  // Yüzde hesaplama fonksiyonu
  const calculatePercentage = (value, total) => {
    if (!total) return 0;
    return Math.round((value / total) * 100);
  };

  // Toplam değerleri hesapla
  const totalFinancialValue =
    totalAmounts.totalInvoiceAmount +
    totalAmounts.totalOverdueInvoiceAmount +
    totalAmounts.totalOverduePaymentAmount +
    totalAmounts.totalOutstandingAmount +
    totalAmounts.fullyPaidAmount +
    totalAmounts.depositPaidAmount;

  // Her kart için yüzde değerleri
  const invoicePercentage = calculatePercentage(
    totalAmounts.totalInvoiceAmount,
    totalFinancialValue
  );
  const overdueInvoicePercentage = calculatePercentage(
    totalAmounts.totalOverdueInvoiceAmount,
    totalFinancialValue
  );
  const overduePaymentPercentage = calculatePercentage(
    totalAmounts.totalOverduePaymentAmount,
    totalFinancialValue
  );

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          color={lightMode ? theme.palette.customRed?.orange : "#ffffff"}
          sx={{
            fontWeight: "700",
            mb: 2,
            borderBottom: `1px dashed ${lightMode ? "#e9ecee" : "#333e47"}`,
            pb: 1,
            textTransform: 'uppercase'
          }}
        >
          {t("summary")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Aktif filtreleri göster */}
          {userFilter !== "all" && (
            <Chip
              size="small"
              label={userFilter}
              color="primary"
              sx={{ height: 24 }}
            />
          )}
          <Chip
            size="small"
            label={getTimeFilterLabel()}
            color={timeFilter === "all" ? "default" : "primary"}
            sx={{ height: 24, textTransform: 'capitalize' }}
          />
          <IconButton
            aria-label="chart options"
            aria-controls={open ? "chart-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={handleClick}
            size="small"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
        <Menu
          id="chart-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "chart-button",
          }}
          sx={{
            "& .MuiPaper-root": {
              boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)",
              borderRadius: "8px",
            },
          }}
        >
          <MenuItem

            onClick={() => handleChartTypeChange("bar")}
            sx={{ py: 1.5, textTransform: 'capitalize' }}
          >
            <Box
              component="span"
              sx={{ mr: 1, display: "inline-flex", alignItems: "center" }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 20V10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 20V4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 20V14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
            {t("barChart")}
          </MenuItem>
          <MenuItem
            onClick={() => handleChartTypeChange("pie")}
            sx={{ py: 1.5, textTransform: 'capitalize' }}
          >
            <Box
              component="span"
              sx={{ mr: 1, display: "inline-flex", alignItems: "center" }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4V12H20C20 16.41 16.41 20 12 20Z"
                  fill="currentColor"
                />
              </svg>
            </Box>
            {t("pieChart")}
          </MenuItem>
        </Menu>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} lg={4}>
              <Box
                sx={{
                  background: "#64B5F6",
                  borderRadius: "12px",
                  padding: ".5rem",
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                  overflow: "hidden",
                  height: "100%",
                  minHeight: "120px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: "10%",
                    right: "5%",
                    width: "100px",
                    height: "100px",
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.05)",
                    zIndex: 1,
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    bottom: "-20%",
                    right: "-10%",
                    width: "150px",
                    height: "150px",
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.03)",
                    zIndex: 1,
                  },
                }}
              >
                <CircularProgressChart
                  value={invoicePercentage}
                  color="#FF5252"
                />

                <Box sx={{ ml: 0, zIndex: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      mb: 0.5,
                    }}
                  >
                    {paraFormat(totalAmounts.totalInvoiceAmount).replace(
                      "£",
                      ""
                    )}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      textTransform: 'capitalize'
                    }}
                  >
                    {t("total")} <br /> {t("invoiceAmount")}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} lg={4}>
              <Box
                sx={{
                  background: "#E53935",
                  borderRadius: "12px",
                  padding: ".5rem",
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                  overflow: "hidden",
                  height: "100%",
                  minHeight: "120px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: "10%",
                    right: "5%",
                    width: "100px",
                    height: "100px",
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.05)",
                    zIndex: 1,
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    bottom: "-20%",
                    right: "-10%",
                    width: "150px",
                    height: "150px",
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.03)",
                    zIndex: 1,
                  },
                }}
              >
                <CircularProgressChart
                  value={overdueInvoicePercentage}
                  color="#FF9800"
                />

                <Box sx={{ ml: 0, zIndex: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      mb: 0.5,
                    }}
                  >
                    {paraFormat(totalAmounts.totalOverdueInvoiceAmount).replace(
                      "£",
                      ""
                    )}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      textTransform: 'capitalize'
                    }}
                  >
                    {t("total")} <br /> {t("overdueInvoice")}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} lg={4}>
              <Box
                sx={{
                  background: "#F06292",
                  borderRadius: "12px",
                  padding: ".5rem",
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                  overflow: "hidden",
                  height: "100%",
                  minHeight: "120px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: "10%",
                    right: "5%",
                    width: "100px",
                    height: "100px",
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.05)",
                    zIndex: 1,
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    bottom: "-20%",
                    right: "-10%",
                    width: "150px",
                    height: "150px",
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.03)",
                    zIndex: 1,
                  },
                }}
              >
                <CircularProgressChart
                  value={overduePaymentPercentage}
                  color="#FF4081"
                />

                <Box sx={{ ml: 0, zIndex: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      mb: 0.5,
                    }}
                  >
                    {paraFormat(totalAmounts.totalOverduePaymentAmount).replace(
                      "£",
                      ""
                    )}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      textTransform: 'capitalize'
                    }}
                  >
                    {t("total")} <br /> {t("overduePayment")}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography variant="body2" sx={{ mr: 1, textTransform: 'capitalize' }}>
              {t("financialDistribution")}
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                color: "text.secondary",
                fontSize: "0.75rem",
                textTransform: 'capitalize'
              }}
            >
              {getChartTypeIcon()}
              {getChartTypeLabel()}
            </Box>
          </Box>

          {renderChart()}
        </Box>
      )}
    </Box>
  );
};

export default FinancialSummaryCharts;
